/**
 * OrgEnrollmentPage.tsx — Public-facing organization sign-up wizard
 *
 * This is the landing page new organizations visit to sign up for the billing
 * platform. No auth required — it guides them through:
 *   Step 1: Organization details (name, email, slug)
 *   Step 2: Email verification
 *   Step 3: Account creation (connects to rule42-auth OIDC)
 *   Step 4: Initial configuration (Stripe connect, first app) — skippable
 *   Step 5: Confirmation & redirect to dashboard
 *
 * @since 2026-06-19 (SaaS conversion — Org Enrollment)
 */
import React, { useState, useCallback } from 'react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface EnrollmentData {
  orgName: string;
  adminEmail: string;
  slug: string;
  adminName: string;
  verificationCode: string;
  password: string;
  firstAppName: string;
  firstAppSlug: string;
  skipSetup: boolean;
}

type Step = 'landing' | 'details' | 'verify' | 'account' | 'configure' | 'complete';

// ---------------------------------------------------------------------------
// Step Components
// ---------------------------------------------------------------------------

function LandingStep({ onStart }: { onStart: () => void }) {
  return (
    <div className="mx-auto max-w-4xl text-center">
      <div className="mb-8">
        <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-cyan-500 to-purple-600 text-2xl text-white shadow-lg">
          ∞
        </div>
        <h1 className="text-4xl font-bold text-slate-900">
          Rule42 Billing Platform
        </h1>
        <p className="mt-4 text-lg text-slate-600">
          Multi-tenant billing infrastructure for SaaS companies.
          Manage customers, subscriptions, invoicing, and payments — all from one platform.
        </p>
      </div>

      <div className="mt-12 grid grid-cols-1 gap-6 text-left sm:grid-cols-3">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="mb-3 text-2xl">💳</div>
          <h3 className="font-semibold text-slate-900">Payment Processing</h3>
          <p className="mt-2 text-sm text-slate-600">Connect your own Stripe account. Process payments, manage subscriptions, handle refunds.</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="mb-3 text-2xl">📊</div>
          <h3 className="font-semibold text-slate-900">Usage-Based Billing</h3>
          <p className="mt-2 text-sm text-slate-600">Meter usage events, define pricing catalogs, and generate invoices automatically.</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="mb-3 text-2xl">🏢</div>
          <h3 className="font-semibold text-slate-900">Multi-App Support</h3>
          <p className="mt-2 text-sm text-slate-600">Manage multiple products under one org with separate plans, catalogs, and customer bases.</p>
        </div>
      </div>

      <div className="mt-12">
        <button
          onClick={onStart}
          className="rounded-xl bg-gradient-to-r from-cyan-600 to-purple-600 px-8 py-4 text-lg font-semibold text-white shadow-lg transition hover:shadow-xl hover:brightness-110"
        >
          Get Started — Create Your Organization
        </button>
        <p className="mt-4 text-sm text-slate-500">
          Already have an account?{' '}
          <a href="/login" className="text-cyan-600 hover:underline">Sign in</a>
        </p>
      </div>
    </div>
  );
}

function DetailsStep({
  data,
  onChange,
  onNext,
  onBack,
  submitting,
  error,
}: {
  data: EnrollmentData;
  onChange: (patch: Partial<EnrollmentData>) => void;
  onNext: () => void;
  onBack: () => void;
  submitting: boolean;
  error: string | null;
}) {
  const slugFromName = (name: string) =>
    name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

  return (
    <div className="mx-auto max-w-lg">
      <h2 className="text-2xl font-bold text-slate-900">Organization Details</h2>
      <p className="mt-2 text-sm text-slate-600">Tell us about your organization. You can change these later.</p>

      <div className="mt-8 space-y-5">
        <div>
          <label className="block text-sm font-medium text-slate-700">Organization Name *</label>
          <input
            type="text"
            value={data.orgName}
            onChange={(e) => {
              onChange({ orgName: e.target.value, slug: slugFromName(e.target.value) });
            }}
            placeholder="Acme Corp"
            className="mt-1 w-full rounded-lg border border-slate-300 px-4 py-3 text-sm focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700">URL Slug</label>
          <div className="mt-1 flex items-center rounded-lg border border-slate-300">
            <span className="px-3 text-sm text-slate-400">finance.rule42.ai/org/</span>
            <input
              type="text"
              value={data.slug}
              onChange={(e) => onChange({ slug: e.target.value })}
              className="flex-1 rounded-r-lg border-0 px-2 py-3 text-sm focus:ring-0"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700">Admin Name *</label>
          <input
            type="text"
            value={data.adminName}
            onChange={(e) => onChange({ adminName: e.target.value })}
            placeholder="Jane Smith"
            className="mt-1 w-full rounded-lg border border-slate-300 px-4 py-3 text-sm focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700">Admin Email *</label>
          <input
            type="email"
            value={data.adminEmail}
            onChange={(e) => onChange({ adminEmail: e.target.value })}
            placeholder="jane@acme.com"
            className="mt-1 w-full rounded-lg border border-slate-300 px-4 py-3 text-sm focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
          />
        </div>

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>
        )}
      </div>

      <div className="mt-8 flex justify-between">
        <button onClick={onBack} className="text-sm text-slate-500 hover:text-slate-700">
          ← Back
        </button>
        <button
          onClick={onNext}
          disabled={!data.orgName || !data.adminEmail || !data.adminName || submitting}
          className="rounded-lg bg-cyan-600 px-6 py-3 text-sm font-medium text-white transition hover:bg-cyan-700 disabled:opacity-50"
        >
          {submitting ? 'Sending verification...' : 'Continue →'}
        </button>
      </div>
    </div>
  );
}

function VerifyStep({
  data,
  onChange,
  onNext,
  onBack,
  onResend,
  submitting,
  error,
}: {
  data: EnrollmentData;
  onChange: (patch: Partial<EnrollmentData>) => void;
  onNext: () => void;
  onBack: () => void;
  onResend: () => void;
  submitting: boolean;
  error: string | null;
}) {
  return (
    <div className="mx-auto max-w-lg text-center">
      <div className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-cyan-100 text-2xl">
        📧
      </div>
      <h2 className="text-2xl font-bold text-slate-900">Verify Your Email</h2>
      <p className="mt-2 text-sm text-slate-600">
        We sent a 6-digit code to <strong>{data.adminEmail}</strong>
      </p>

      <div className="mx-auto mt-8 max-w-xs">
        <input
          type="text"
          value={data.verificationCode}
          onChange={(e) => onChange({ verificationCode: e.target.value.replace(/\D/g, '').slice(0, 6) })}
          placeholder="000000"
          maxLength={6}
          className="w-full rounded-lg border border-slate-300 px-4 py-4 text-center text-2xl tracking-[0.5em] focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
        />
      </div>

      {error && (
        <div className="mx-auto mt-4 max-w-xs rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>
      )}

      <div className="mt-6">
        <button onClick={onResend} className="text-sm text-cyan-600 hover:underline">
          Didn't receive a code? Resend
        </button>
      </div>

      <div className="mt-8 flex justify-between">
        <button onClick={onBack} className="text-sm text-slate-500 hover:text-slate-700">
          ← Back
        </button>
        <button
          onClick={onNext}
          disabled={data.verificationCode.length < 6 || submitting}
          className="rounded-lg bg-cyan-600 px-6 py-3 text-sm font-medium text-white transition hover:bg-cyan-700 disabled:opacity-50"
        >
          {submitting ? 'Verifying...' : 'Verify & Continue →'}
        </button>
      </div>
    </div>
  );
}

function AccountStep({
  data,
  onChange,
  onNext,
  onBack,
  submitting,
  error,
}: {
  data: EnrollmentData;
  onChange: (patch: Partial<EnrollmentData>) => void;
  onNext: () => void;
  onBack: () => void;
  submitting: boolean;
  error: string | null;
}) {
  return (
    <div className="mx-auto max-w-lg">
      <h2 className="text-2xl font-bold text-slate-900">Create Your Account</h2>
      <p className="mt-2 text-sm text-slate-600">
        Set a password for your admin account. You'll use this to sign in to the billing dashboard.
      </p>

      <div className="mt-8 space-y-5">
        <div>
          <label className="block text-sm font-medium text-slate-700">Email</label>
          <input
            type="email"
            value={data.adminEmail}
            disabled
            className="mt-1 w-full rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700">Password *</label>
          <input
            type="password"
            value={data.password}
            onChange={(e) => onChange({ password: e.target.value })}
            placeholder="Minimum 8 characters"
            className="mt-1 w-full rounded-lg border border-slate-300 px-4 py-3 text-sm focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
          />
          <p className="mt-1 text-xs text-slate-500">Must be at least 8 characters with one uppercase and one number.</p>
        </div>

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>
        )}
      </div>

      <div className="mt-8 flex justify-between">
        <button onClick={onBack} className="text-sm text-slate-500 hover:text-slate-700">
          ← Back
        </button>
        <button
          onClick={onNext}
          disabled={data.password.length < 8 || submitting}
          className="rounded-lg bg-cyan-600 px-6 py-3 text-sm font-medium text-white transition hover:bg-cyan-700 disabled:opacity-50"
        >
          {submitting ? 'Creating account...' : 'Create Account →'}
        </button>
      </div>
    </div>
  );
}

function ConfigureStep({
  data,
  onChange,
  onNext,
  onSkip,
  submitting,
}: {
  data: EnrollmentData;
  onChange: (patch: Partial<EnrollmentData>) => void;
  onNext: () => void;
  onSkip: () => void;
  submitting: boolean;
}) {
  return (
    <div className="mx-auto max-w-lg">
      <h2 className="text-2xl font-bold text-slate-900">Initial Configuration</h2>
      <p className="mt-2 text-sm text-slate-600">
        Set up your first application. You can skip this and configure later from your dashboard.
      </p>

      <div className="mt-8 space-y-5">
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-5">
          <h3 className="font-medium text-slate-900">Your First Application</h3>
          <p className="mt-1 text-xs text-slate-500">Applications represent products you want to bill for.</p>
          <div className="mt-4 space-y-3">
            <input
              type="text"
              value={data.firstAppName}
              onChange={(e) => onChange({ firstAppName: e.target.value })}
              placeholder="My SaaS Product"
              className="w-full rounded-lg border border-slate-300 px-4 py-3 text-sm focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
            />
            <input
              type="text"
              value={data.firstAppSlug}
              onChange={(e) => onChange({ firstAppSlug: e.target.value })}
              placeholder="my-saas (URL-safe identifier)"
              className="w-full rounded-lg border border-slate-300 px-4 py-3 text-sm focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
            />
          </div>
        </div>

        <div className="rounded-xl border border-cyan-200 bg-cyan-50 p-5">
          <h3 className="font-medium text-slate-900">Payment Provider (Stripe)</h3>
          <p className="mt-1 text-xs text-slate-500">Connect your Stripe account to start accepting payments. You can do this later from Settings.</p>
          <p className="mt-3 text-sm text-cyan-700">→ You'll configure Stripe from your org dashboard after enrollment.</p>
        </div>
      </div>

      <div className="mt-8 flex justify-between">
        <button onClick={onSkip} className="text-sm text-slate-500 hover:text-slate-700">
          Skip for now →
        </button>
        <button
          onClick={onNext}
          disabled={submitting}
          className="rounded-lg bg-cyan-600 px-6 py-3 text-sm font-medium text-white transition hover:bg-cyan-700 disabled:opacity-50"
        >
          {submitting ? 'Saving...' : 'Save & Continue →'}
        </button>
      </div>
    </div>
  );
}

function CompleteStep({ orgName, onGoToDashboard }: { orgName: string; onGoToDashboard: () => void }) {
  return (
    <div className="mx-auto max-w-lg text-center">
      <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-green-100 text-3xl">
        ✓
      </div>
      <h2 className="text-2xl font-bold text-slate-900">Welcome to Rule42 Billing!</h2>
      <p className="mt-3 text-slate-600">
        <strong>{orgName}</strong> has been enrolled successfully.
      </p>

      <div className="mt-8 rounded-xl border border-slate-200 bg-slate-50 p-6 text-left">
        <h3 className="font-medium text-slate-900">Next Steps</h3>
        <ul className="mt-3 space-y-2 text-sm text-slate-600">
          <li className="flex items-start gap-2">
            <span className="mt-0.5 text-cyan-500">●</span>
            Connect your Stripe account in Settings → Payment Provider
          </li>
          <li className="flex items-start gap-2">
            <span className="mt-0.5 text-cyan-500">●</span>
            Create your first plan in Catalog → Plans
          </li>
          <li className="flex items-start gap-2">
            <span className="mt-0.5 text-cyan-500">●</span>
            Add your first customer in Customers → Accounts
          </li>
          <li className="flex items-start gap-2">
            <span className="mt-0.5 text-cyan-500">●</span>
            Set up webhooks for real-time billing events
          </li>
        </ul>
      </div>

      <button
        onClick={onGoToDashboard}
        className="mt-8 rounded-xl bg-gradient-to-r from-cyan-600 to-purple-600 px-8 py-4 text-lg font-semibold text-white shadow-lg transition hover:shadow-xl hover:brightness-110"
      >
        Go to Your Dashboard →
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Progress Indicator
// ---------------------------------------------------------------------------

function StepIndicator({ current, steps }: { current: number; steps: string[] }) {
  return (
    <div className="mb-10 flex items-center justify-center gap-2">
      {steps.map((label, i) => (
        <React.Fragment key={label}>
          <div className="flex items-center gap-2">
            <div
              className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold ${
                i < current
                  ? 'bg-cyan-600 text-white'
                  : i === current
                  ? 'border-2 border-cyan-600 text-cyan-600'
                  : 'border border-slate-300 text-slate-400'
              }`}
            >
              {i < current ? '✓' : i + 1}
            </div>
            <span className={`hidden text-xs sm:inline ${i <= current ? 'text-slate-700' : 'text-slate-400'}`}>
              {label}
            </span>
          </div>
          {i < steps.length - 1 && (
            <div className={`h-px w-8 ${i < current ? 'bg-cyan-600' : 'bg-slate-300'}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

const STEP_LABELS = ['Details', 'Verify', 'Account', 'Configure', 'Done'];

// Determine API base URL from window location or environment
function getApiBaseUrl(): string {
  // Check if we're embedded with a known API URL
  if (typeof window !== 'undefined') {
    const meta = document.querySelector('meta[name="api-base-url"]');
    if (meta) return meta.getAttribute('content') ?? '';
  }
  // Default: same origin
  return '';
}

export default function OrgEnrollmentPage(): React.JSX.Element {
  const [step, setStep] = useState<Step>('landing');
  const [data, setData] = useState<EnrollmentData>({
    orgName: '',
    adminEmail: '',
    slug: '',
    adminName: '',
    verificationCode: '',
    password: '',
    firstAppName: '',
    firstAppSlug: '',
    skipSetup: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const apiBase = getApiBaseUrl();

  const update = useCallback((patch: Partial<EnrollmentData>) => {
    setData((prev) => ({ ...prev, ...patch }));
    setError(null);
  }, []);

  const stepIndex = ['landing', 'details', 'verify', 'account', 'configure', 'complete'].indexOf(step) - 1;

  // Step handlers
  async function handleDetailsNext() {
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`${apiBase}/api/v1/enrollment/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          orgName: data.orgName,
          adminEmail: data.adminEmail,
          adminName: data.adminName,
          slug: data.slug,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as { error?: string }).error ?? `Request failed: ${res.status}`);
      }
      setStep('verify');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start enrollment');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleVerifyNext() {
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`${apiBase}/api/v1/enrollment/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: data.adminEmail,
          code: data.verificationCode,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as { error?: string }).error ?? 'Invalid verification code');
      }
      setStep('account');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleResendCode() {
    try {
      await fetch(`${apiBase}/api/v1/enrollment/resend`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: data.adminEmail }),
      });
    } catch {
      // silently fail resend
    }
  }

  async function handleAccountNext() {
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`${apiBase}/api/v1/enrollment/create-account`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: data.adminEmail,
          password: data.password,
          name: data.adminName,
          orgName: data.orgName,
          slug: data.slug,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as { error?: string }).error ?? 'Account creation failed');
      }
      setStep('configure');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Account creation failed');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleConfigureNext() {
    setSubmitting(true);
    try {
      await fetch(`${apiBase}/api/v1/enrollment/configure`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: data.adminEmail,
          appName: data.firstAppName,
          appSlug: data.firstAppSlug,
        }),
      });
      setStep('complete');
    } catch {
      setStep('complete'); // Non-critical — they can configure later
    } finally {
      setSubmitting(false);
    }
  }

  function handleGoToDashboard() {
    window.location.href = '/login';
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Minimal header */}
      <header className="border-b border-slate-200 bg-white/80 px-8 py-4 backdrop-blur">
        <div className="mx-auto flex max-w-5xl items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-cyan-500 to-purple-600 text-sm font-bold text-white">
              ∞
            </div>
            <span className="text-lg font-semibold text-slate-900">Rule42 Billing</span>
          </a>
          <a href="/login" className="text-sm text-slate-600 hover:text-cyan-600">
            Sign in
          </a>
        </div>
      </header>

      {/* Content */}
      <main className="mx-auto max-w-5xl px-8 py-12">
        {step !== 'landing' && stepIndex >= 0 && (
          <StepIndicator current={stepIndex} steps={STEP_LABELS} />
        )}

        {step === 'landing' && <LandingStep onStart={() => setStep('details')} />}
        {step === 'details' && (
          <DetailsStep
            data={data}
            onChange={update}
            onNext={handleDetailsNext}
            onBack={() => setStep('landing')}
            submitting={submitting}
            error={error}
          />
        )}
        {step === 'verify' && (
          <VerifyStep
            data={data}
            onChange={update}
            onNext={handleVerifyNext}
            onBack={() => setStep('details')}
            onResend={handleResendCode}
            submitting={submitting}
            error={error}
          />
        )}
        {step === 'account' && (
          <AccountStep
            data={data}
            onChange={update}
            onNext={handleAccountNext}
            onBack={() => setStep('verify')}
            submitting={submitting}
            error={error}
          />
        )}
        {step === 'configure' && (
          <ConfigureStep
            data={data}
            onChange={update}
            onNext={handleConfigureNext}
            onSkip={() => setStep('complete')}
            submitting={submitting}
          />
        )}
        {step === 'complete' && (
          <CompleteStep orgName={data.orgName} onGoToDashboard={handleGoToDashboard} />
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 py-6 text-center text-xs text-slate-400">
        © 2026 Rule42. Multi-tenant billing infrastructure.
      </footer>
    </div>
  );
}
